import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FilterPanel = ({ filters, onFiltersChange, onApplyFilters, onResetFilters }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const contractOptions = [
    { value: 'all', label: 'All Contracts' },
    { value: 'eua-dec24', label: 'EUA Dec 2024' },
    { value: 'eua-dec25', label: 'EUA Dec 2025' },
    { value: 'eua-dec26', label: 'EUA Dec 2026' },
    { value: 'brent-crude', label: 'Brent Crude Oil' },
    { value: 'wti-crude', label: 'WTI Crude Oil' },
    { value: 'heating-oil', label: 'Heating Oil' },
    { value: 'natural-gas', label: 'Natural Gas' }
  ];

  const exchangeOptions = [
    { value: 'all', label: 'All Exchanges' },
    { value: 'ice', label: 'ICE (Intercontinental Exchange)' },
    { value: 'nymex', label: 'NYMEX' },
    { value: 'cme', label: 'CME Group' },
    { value: 'eex', label: 'European Energy Exchange' }
  ];

  const granularityOptions = [
    { value: 'tick', label: 'Tick Data' },
    { value: '1min', label: '1 Minute' },
    { value: '5min', label: '5 Minutes' },
    { value: '15min', label: '15 Minutes' },
    { value: '1hour', label: '1 Hour' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const dataTypeOptions = [
    { value: 'all', label: 'All Data Types' },
    { value: 'prices', label: 'Prices Only' },
    { value: 'volume', label: 'Volume Only' },
    { value: 'open-interest', label: 'Open Interest' },
    { value: 'settlement', label: 'Settlement Prices' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Filter" size={20} />
          Advanced Filters
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
        </Button>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              value={filters?.startDate}
              onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
            />
            <Input
              label="End Date"
              type="date"
              value={filters?.endDate}
              onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
            />
          </div>

          {/* Contract and Exchange */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Contract Type"
              options={contractOptions}
              value={filters?.contract}
              onChange={(value) => handleFilterChange('contract', value)}
            />
            <Select
              label="Exchange"
              options={exchangeOptions}
              value={filters?.exchange}
              onChange={(value) => handleFilterChange('exchange', value)}
            />
          </div>

          {/* Data Granularity and Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Data Granularity"
              options={granularityOptions}
              value={filters?.granularity}
              onChange={(value) => handleFilterChange('granularity', value)}
            />
            <Select
              label="Data Type"
              options={dataTypeOptions}
              value={filters?.dataType}
              onChange={(value) => handleFilterChange('dataType', value)}
            />
          </div>

          {/* Price Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Min Price"
              type="number"
              placeholder="0.00"
              value={filters?.minPrice}
              onChange={(e) => handleFilterChange('minPrice', e?.target?.value)}
            />
            <Input
              label="Max Price"
              type="number"
              placeholder="1000.00"
              value={filters?.maxPrice}
              onChange={(e) => handleFilterChange('maxPrice', e?.target?.value)}
            />
          </div>

          {/* Volume Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Min Volume"
              type="number"
              placeholder="0"
              value={filters?.minVolume}
              onChange={(e) => handleFilterChange('minVolume', e?.target?.value)}
            />
            <Input
              label="Max Volume"
              type="number"
              placeholder="1000000"
              value={filters?.maxVolume}
              onChange={(e) => handleFilterChange('maxVolume', e?.target?.value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-border">
            <Button
              variant="default"
              onClick={onApplyFilters}
              iconName="Search"
              iconPosition="left"
              className="flex-1"
            >
              Apply Filters
            </Button>
            <Button
              variant="outline"
              onClick={onResetFilters}
              iconName="RotateCcw"
              iconPosition="left"
              className="flex-1"
            >
              Reset Filters
            </Button>
          </div>

          {/* Quick Filters */}
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-foreground mb-3">Quick Filters</h4>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFilterChange('quickFilter', 'last-week')}
              >
                Last Week
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFilterChange('quickFilter', 'last-month')}
              >
                Last Month
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFilterChange('quickFilter', 'last-quarter')}
              >
                Last Quarter
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFilterChange('quickFilter', 'last-year')}
              >
                Last Year
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFilterChange('quickFilter', 'ytd')}
              >
                Year to Date
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;